import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';

// Shopify Storefront API configuration
const SHOPIFY_DOMAIN =
  import.meta.env.VITE_SHOPIFY_STORE_DOMAIN || '5pwxmm-ce.myshopify.com';
const SHOPIFY_API_VERSION =
  import.meta.env.VITE_SHOPIFY_API_VERSION || '2025-01';
// Environment variable from GitHub Actions secrets
const SHOPIFY_TOKEN = import.meta.env.VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN;

const httpLink = createHttpLink({
  uri: `https://${SHOPIFY_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`,
});

const authLink = setContext((_, { headers }) => {
  const token = SHOPIFY_TOKEN;

  if (!token) {
    console.error('Shopify token not available - products will not load');
    return { headers };
  }

  return {
    headers: {
      ...headers,
      'X-Shopify-Storefront-Access-Token': token,
      'Content-Type': 'application/json',
    },
  };
});

// Apollo Client with Shopify-specific configuration
export const shopifyClient = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache({
    typePolicies: {
      Product: {
        keyFields: ['id'],
      },
      ProductVariant: {
        keyFields: ['id'],
      },
      Collection: {
        keyFields: ['id'],
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
  },
});

// Shopify GraphQL Types
export interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  description: string;
  tags: string[];
  priceRange: {
    minVariantPrice: {
      amount: string;
      currencyCode: string;
    };
  };
  images: {
    edges: Array<{
      node: {
        url: string;
        altText?: string;
      };
    }>;
  };
  variants: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        availableForSale: boolean;
        price: {
          amount: string;
          currencyCode: string;
        };
      };
    }>;
  };
  metafields: Array<{
    namespace: string;
    key: string;
    value: string;
    type: string;
  }>;
}

export interface ShopifyProductsResponse {
  products: {
    edges: Array<{
      node: ShopifyProduct;
    }>;
  };
}

export interface ShopifyProductResponse {
  product: ShopifyProduct;
}

export interface ShopifyProductsPaginatedResponse {
  products: {
    pageInfo: {
      hasNextPage: boolean;
      endCursor: string | null;
    };
    edges: Array<{
      cursor: string;
      node: ShopifyProduct;
    }>;
  };
}

// Shopify Blog/Recipe Types
export interface ShopifyArticle {
  id: string;
  title: string;
  handle: string;
  excerpt: string | null;
  content: string;
  contentHtml: string;
  image: {
    url: string;
    altText: string | null;
  } | null;
  publishedAt: string;
  author: {
    firstName: string;
    lastName: string;
  } | null;
  tags: string[];
  metafields: Array<{
    namespace: string;
    key: string;
    value: string;
    type: string;
  }>;
}

export interface ShopifyRecipesResponse {
  blogs: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        handle: string;
        articles: {
          edges: Array<{
            node: ShopifyArticle;
          }>;
        };
      };
    }>;
  };
}

export interface ShopifyRecipeResponse {
  blogs: {
    edges: Array<{
      node: {
        articleByHandle: ShopifyArticle | null;
      };
    }>;
  };
}
